import React, { useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { FormattedMessage, useIntl } from 'react-intl';
import { I_Installation } from '../../../interfaces/InstallationTypes';
import { Route, Routes, useLocation } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import FlatInstallationView from './FlatInstallationView';
import SodioHomeInstallation from './Installation';
import { EMS_KNOWN, emsBucket } from '../../../utils/externalEms';
import {
  normalizePartner,
  partnerOptions as buildPartnerOptions
} from '../../../utils/partnerName';

interface installationSearchProps {
  installations: I_Installation[];
  product?: number;
}

// The dropdown values below double as filter state, so they stay the original
// English strings; only the labels are translated. Changing a value would
// silently reset a user's filter, changing a label costs nothing.
const ALL = 'All Installations';

const STATUS_FILTERS = [
  { value: ALL, id: 'filterAllInstallations', defaultMessage: 'All Installations' },
  { value: 'Installations With Alarm', id: 'filterWithAlarm', defaultMessage: 'Installations With Alarm' },
  { value: 'Installations with Warning', id: 'filterWithWarning', defaultMessage: 'Installations With Warning' },
  { value: 'Functional Installations', id: 'filterFunctional', defaultMessage: 'Functional Installations' },
  { value: 'Offline Installations', id: 'filterOffline', defaultMessage: 'Offline Installations' },
  { value: 'Installations Without Data Collection', id: 'filterWithoutDataCollection', defaultMessage: 'Installations Without Data Collection' }
];

const ACTION_FILTERS = [
  { value: ALL, id: 'filterAllInstallations', defaultMessage: 'All Installations' },
  { value: 'Installations With Action Flag', id: 'filterWithActionFlag', defaultMessage: 'Installations With Action Flag' },
  { value: 'Installations Without Action Flag', id: 'filterWithoutActionFlag', defaultMessage: 'Installations Without Action Flag' }
];

// Sentinels for the two facet dropdowns. None can collide with a stored value
// because no EMS or partner is named like this. ANY is deliberately non-empty:
// an outlined MUI Select only shrinks its floating label once the value is
// non-empty, so an '' default would print the label on top of "All".
const ANY = '__all__';
const NOT_SET = '__notset__';
const OTHER = '__other__';

function InstallationSearch(props: installationSearchProps) {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByStatus, setSortByStatus] = useState(ALL);
  const [sortByAction, setSortByAction] = useState(ALL);
  const [emsFilter, setEmsFilter] = useState(ANY);
  const [partnerFilter, setPartnerFilter] = useState(ANY);
  const currentLocation = useLocation();
  const baseRoute = props.product === 5 ? routes.sodistorepro_installations : routes.sodiohome_installations;

  const indexedData = useMemo(() => {
    return props.installations.map((item) => ({
      ...item,
      nameLower: item.name.toLowerCase(),
      locationLower: item.location.toLowerCase(),
      regionLower: item.region.toLowerCase(),
      // The flat list already shows an Installation SN column, so the search box
      // has to match it — that is the field people actually have on hand.
      serialLower: (item.serialNumber ?? '').toLowerCase()
    }));
  }, [props.installations]);

  // Built from the full list, not the filtered one, so options do not disappear
  // as you narrow down. Every partner offered therefore matches at least one row.
  const partnerChoices = useMemo(
    () => buildPartnerOptions(props.installations.map((i) => i.distributionPartner)),
    [props.installations]
  );

  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let list = indexedData.filter(
      (item) =>
        item.nameLower.includes(term) ||
        item.locationLower.includes(term) ||
        item.regionLower.includes(term) ||
        item.serialLower.includes(term)
    );

    switch (sortByStatus) {
      case 'Installations With Alarm':
        list = list.filter((i) => i.status === 2);
        break;
      case 'Installations with Warning':
        list = list.filter((i) => i.status === 1);
        break;
      case 'Functional Installations':
        list = list.filter((i) => i.status === 0);
        break;
      case 'Offline Installations':
        list = list.filter((i) => i.status === -1);
        break;
      case 'Installations Without Data Collection':
        list = list.filter((i) => i.dataCollectionEnabled === false);
        break;
    }

    switch (sortByAction) {
      case 'Installations With Action Flag':
        list = list.filter((i) => i.testingMode === true);
        break;
      case 'Installations Without Action Flag':
        list = list.filter((i) => i.testingMode === false);
        break;
    }

    // "Other" is a bucket, not a stored value: picking it must return every site
    // whose EMS was typed into the free-text box, not just the ones left on the
    // literal string 'Other'.
    if (emsFilter === NOT_SET) {
      list = list.filter((i) => emsBucket(i.externalEms) === 'notSet');
    } else if (emsFilter === OTHER) {
      list = list.filter((i) => emsBucket(i.externalEms) === 'other');
    } else if (emsFilter !== ANY) {
      // Trimmed to agree with emsBucket: an untrimmed compare would leave a
      // stored ' Loxone ' bucketed as known yet matching no option, so it would
      // vanish from every EMS filter instead of just one.
      list = list.filter((i) => (i.externalEms ?? '').trim() === emsFilter);
    }

    if (partnerFilter === NOT_SET) {
      list = list.filter((i) => !i.distributionPartner || i.distributionPartner.trim() === '');
    } else if (partnerFilter !== ANY) {
      list = list.filter(
        (i) => normalizePartner(i.distributionPartner) === normalizePartner(partnerFilter)
      );
    }

    return list;
  }, [searchTerm, indexedData, sortByStatus, sortByAction, emsFilter, partnerFilter]);

  const filtersActive =
    searchTerm !== '' ||
    sortByStatus !== ALL ||
    sortByAction !== ALL ||
    emsFilter !== ANY ||
    partnerFilter !== ANY;

  const resetFilters = () => {
    setSearchTerm('');
    setSortByStatus(ALL);
    setSortByAction(ALL);
    setEmsFilter(ANY);
    setPartnerFilter(ANY);
  };

  const isListView =
    currentLocation.pathname === baseRoute + 'list' ||
    currentLocation.pathname === baseRoute + routes.list;

  return (
    <>
      {isListView && (
        <Grid container>
          <Grid item xs={12}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                flexWrap: 'wrap'
              }}
            >
              <FormControl variant="outlined" sx={{ width: 280 }}>
                <TextField
                  placeholder={intl.formatMessage({ id: 'searchNameOrSn' })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchTwoToneIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </FormControl>

              <FormControl sx={{ width: 280 }}>
                <InputLabel>
                  <FormattedMessage id="sortByStatus" defaultMessage="Sort By Status" />
                </InputLabel>
                <Select
                  value={sortByStatus}
                  onChange={(e) => setSortByStatus(e.target.value)}
                  label={intl.formatMessage({ id: 'sortByStatus' })}
                >
                  {STATUS_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {intl.formatMessage({
                        id: option.id,
                        defaultMessage: option.defaultMessage
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: 280 }}>
                <InputLabel>
                  <FormattedMessage id="sortByActionFlag" defaultMessage="Sort By Action Flag" />
                </InputLabel>
                <Select
                  value={sortByAction}
                  onChange={(e) => setSortByAction(e.target.value)}
                  label={intl.formatMessage({ id: 'sortByActionFlag' })}
                >
                  {ACTION_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {intl.formatMessage({
                        id: option.id,
                        defaultMessage: option.defaultMessage
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: 280 }}>
                <InputLabel>
                  <FormattedMessage id="externalEms" defaultMessage="External EMS" />
                </InputLabel>
                <Select
                  value={emsFilter}
                  onChange={(e) => setEmsFilter(e.target.value)}
                  label={intl.formatMessage({ id: 'externalEms' })}
                >
                  <MenuItem value={ANY}>
                    <FormattedMessage id="filterAllEms" defaultMessage="All" />
                  </MenuItem>
                  <MenuItem value={NOT_SET}>
                    <em>
                      <FormattedMessage id="notSet" defaultMessage="Not set" />
                    </em>
                  </MenuItem>
                  {EMS_KNOWN.map((ems) => (
                    <MenuItem key={ems} value={ems}>
                      {ems === 'No' ? (
                        <FormattedMessage id="emsNo" defaultMessage="No" />
                      ) : (
                        ems
                      )}
                    </MenuItem>
                  ))}
                  <MenuItem value={OTHER}>
                    <FormattedMessage id="emsOther" defaultMessage="Other" />
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ width: 280 }}>
                <InputLabel>
                  <FormattedMessage
                    id="distributionPartner"
                    defaultMessage="Distribution Partner"
                  />
                </InputLabel>
                <Select
                  value={partnerFilter}
                  onChange={(e) => setPartnerFilter(e.target.value)}
                  label={intl.formatMessage({ id: 'distributionPartner' })}
                >
                  <MenuItem value={ANY}>
                    <FormattedMessage id="filterAllPartners" defaultMessage="All" />
                  </MenuItem>
                  {/* Rendered unconditionally: gating it on "some installation
                      has a blank partner" would unmount the option while it is
                      the selected value the moment the last blank one is filled
                      in, leaving the Select blank and the table empty. */}
                  <MenuItem value={NOT_SET}>
                    <em>
                      <FormattedMessage id="notSet" defaultMessage="Not set" />
                    </em>
                  </MenuItem>
                  {partnerChoices.map((partner) => (
                    <MenuItem key={partner} value={partner}>
                      {partner}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                <FormattedMessage
                  id="showingXofY"
                  defaultMessage="Showing {shown} of {total}"
                  values={{
                    shown: filteredData.length,
                    total: props.installations.length
                  }}
                />
              </Typography>

              <Button size="small" onClick={resetFilters} disabled={!filtersActive}>
                <FormattedMessage id="resetFilters" defaultMessage="Reset filters" />
              </Button>
            </div>
          </Grid>
        </Grid>
      )}

      <FlatInstallationView installations={filteredData} product={props.product} />
      <Routes>
        {/* Routes come from the unfiltered list on purpose: an installation you
            have open must not lose its route because a filter stopped matching it. */}
        {props.installations.map((installation) => {
          return (
            <Route
              key={installation.id}
              path={routes.installation + installation.id + '/*'}
              element={
                <SodioHomeInstallation
                  key={installation.id}
                  current_installation={installation}
                  type="installation"
                ></SodioHomeInstallation>
              }
            />
          );
        })}
      </Routes>
    </>
  );
}

export default InstallationSearch;
